import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  ASSIGNMENT_BADGES,
  getBadgeColor,
  getBadgeTextColor,
  getBadgeBorderColor,
  getAssignmentTooltipMyPerspective,
  getStatusText
} from '../../../constants/assignmentStatuses';

const tooltipConfig = {
  arrow: true,
  placement: 'top',
  enterDelay: 500,
  leaveDelay: 200
};

/**
 * ADR-014 §10k — "this assignment was frozen before it finished".
 *
 * Distinct from `SessionStatusChip`, which describes the SESSION. This one is
 * about the individual piece of work: the session ended while this assignment
 * was still open, so it stopped where it stopped and can never be finished.
 *
 * The chip label stays short — "Left Incomplete" — and names the assignment,
 * because it sits in the assignment-status slot where "Completed" is its peer.
 * Pass `status` (an ASSIGNMENT_STATUSES value) to add where the work stopped to
 * the TOOLTIP rather than the label: "Left Incomplete · In Progress" reads
 * redundantly once the label already says the work never finished.
 *
 * @param {string} [status] - ASSIGNMENT_STATUSES value the work was frozen at;
 *   adds "It was left at: …" to the tooltip. Omit on surfaces that don't have it.
 * @param {Object} [sx] - MUI style overrides merged over the chip defaults
 */
export const AssignmentFrozenBadge = ({ status = null, sx = {} }) => {
  const statusText = status ? getStatusText(status) : null;
  const tooltip = statusText
    ? `${getAssignmentTooltipMyPerspective(ASSIGNMENT_BADGES.LEFT_INCOMPLETE)} It was left at: ${statusText}.`
    : getAssignmentTooltipMyPerspective(ASSIGNMENT_BADGES.LEFT_INCOMPLETE);

  return (
    <Tooltip title={tooltip} {...tooltipConfig}>
      <Chip
        label={ASSIGNMENT_BADGES.LEFT_INCOMPLETE}
        sx={{
          backgroundColor: getBadgeColor(ASSIGNMENT_BADGES.LEFT_INCOMPLETE),
          color: getBadgeTextColor(ASSIGNMENT_BADGES.LEFT_INCOMPLETE),
          fontWeight: 500,
          fontSize: '13px',
          border: '1px solid',
          borderColor: getBadgeBorderColor(ASSIGNMENT_BADGES.LEFT_INCOMPLETE),
          '& .MuiChip-label': {
            px: 2
          },
          ...sx
        }}
      />
    </Tooltip>
  );
};

export default AssignmentFrozenBadge;
